import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

export interface ImageResizeControlsProps {
  width: number;
  height: number;
  onResize: (width: number, height: number) => void;
  onDelete: () => void;
  onClose: () => void;
  maintainAspectRatio?: boolean;
}

export const ImageResizeControls: React.FC<ImageResizeControlsProps> = ({
  width,
  height,
  onResize,
  onDelete,
  onClose,
  maintainAspectRatio = true
}) => {
  const [localWidth, setLocalWidth] = React.useState(width);
  const [localHeight, setLocalHeight] = React.useState(height);
  const [keepAspectRatio, setKeepAspectRatio] = React.useState(maintainAspectRatio);
  const aspectRatio = React.useRef(width / height);

  React.useEffect(() => {
    setLocalWidth(width);
    setLocalHeight(height);
    aspectRatio.current = width / height;
  }, [width, height]);

  const handleWidthChange = (newWidth: number) => {
    setLocalWidth(newWidth);
    if (keepAspectRatio) {
      setLocalHeight(Math.round(newWidth / aspectRatio.current));
    }
  };

  const handleHeightChange = (newHeight: number) => {
    setLocalHeight(newHeight);
    if (keepAspectRatio) {
      setLocalWidth(Math.round(newHeight * aspectRatio.current));
    }
  };

  const handleApply = () => {
    onResize(localWidth, localHeight);
    onClose();
  };

  return (
    <div className="absolute z-50 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 p-4 min-w-64">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">Resize Image</h4>
          <button
            onClick={onClose}
            className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-400 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label
              htmlFor="width"
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Width (px)
            </label>
            <input
              id="width"
              type="number"
              value={localWidth}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              min="1"
            />
          </div>
          <div className="flex-1">
            <label
              htmlFor="height"
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              Height (px)
            </label>
            <input
              id="height"
              type="number"
              value={localHeight}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              className="w-full px-2 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              min="1"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            checked={keepAspectRatio}
            onChange={(e) => setKeepAspectRatio(e.target.checked)}
            className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
          />
          <span>Maintain aspect ratio</span>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleApply}
            className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors"
          >
            Apply
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 border border-red-600 rounded transition-colors flex items-center gap-1.5"
            aria-label="Delete image"
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};
